import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Grid, Typography, Tabs, Tab, Stack } from '@mui/material';
import { handleExportError } from "../../utils/snackBarUtil";
import { fetchPowerBIKPIData, fetchUserActivityGrowthData, fetchUserActivityTrendData, fetchUserActivityTableData, exportUserActivityDetails, fetchUserActivityFilterOptions, fetchUserActivityUsersFilter } from '../../service/service';
import SectorCheckFilter from '../Dropdown/SectorCheckFilter';
import NumberWidget from '../Widgets/NumberWidget';
import MultiLineWidget from '../Widgets/MultiLineChart';
import { CustomizedDataGrid } from '../customized_data_grid';
import CustomDropdown from '../Dropdown';
import DateTimeRangeFilter from 'components/Dropdown/DateTimeRangeFilter/DateTimeRangeFilter';
import ReactECharts from 'echarts-for-react';
import LoaderComponent from '../Loader';
import { generateSectorColors } from '../../utils/sectorColorUtils';
import { getPepwiseSectorList } from 'service/service';
import '../PowerBIObservability/powerBIObs.scss';
// Import components for tabs
import ReportsTab from './UserActivity/ReportsTab';
import CapacityTab from './UserActivity/CapacityTab';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import { useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from 'react-router-dom';
import { CapacityNameFormatter, LastLoginFormatter, MultiSectorAccessFormatter, ReportNameFormatter, SectorFormatter, StatusFormatter, UserFormatter, UserTypeFormatter, WorkspaceNameFormatter } from './UserActivity/formatters';
import { formatValue, createLabelFormatter, processFilterKey, handleActivityStatusChange, getSelectedSectors, getSearchAfterValue, buildTableDataRequest, handleSearchAfterStorage, appendTableData, replaceTableData } from './UserActivity/helper';
import { useHeader } from 'Context/contexts/HeaderTabRightContentContext';
import PageWrapper from 'components/Common/PageWraper';

// Inline User Activity Table Components
interface TableRow {
	user: string;
	sector: string;
	userType: string;
	lastLogin: string;
	multiSectorAccess: string;
	activityStatus: 'Active' | 'At risk' | 'Inactive';
	workspaceName: string;
	capacityName: string;
	reportName: string;
}

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

function TabPanel(props: Readonly<TabPanelProps>) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`powerbi-tabpanel-${index}`}
			aria-labelledby={`powerbi-tab-${index}`}
			{...other}
		>
			{value === index && (
				<Box>
					{children}
				</Box>
			)}
		</div>
	);
}

function a11yProps(index: number) {
	return {
		id: `powerbi-tab-${index}`,
		'aria-controls': `powerbi-tabpanel-${index}`,
	};
}

function useWindowSize() {
	const [size, setSize] = useState({ width: 0, height: 0 });

	useEffect(() => {
		function handleResize() {
			setSize({ width: window.innerWidth, height: window.innerHeight });
		}

		handleResize(); // set on mount
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	return size;
}

const POWERBI_DATE_FIXED_OPTIONS = [
	{ val: 'today', label: 'Today' },
	{ val: 'yesterday', label: 'Yesterday' },
	{ val: '7d', label: 'Last 7 Days' },
	{ val: '30d', label: 'Last 30 Days' },
	{ val: '60d', label: 'Last 60 Days' },
	{ val: '90d', label: 'Last 90 Days' }
];

const POWERBI_DATE_RANGE_MAP: { [key: string]: string } = {
	today: 'TODAY',
	yesterday: 'YESTERDAY',
	'7d': 'LAST_7_DAYS',
	'30d': 'LAST_30_DAYS',
	'60d': 'LAST_60_DAYS',
	'90d': 'LAST_90_DAYS'
};

const PowerBIUserActivityDashboard: React.FC = () => {
	const theme = useTheme();
	const { setRightContextContent } = useHeader();
	const location = useLocation();
	const navigate = useNavigate();
	const [tabValue, setTabValue] = useState<number>(
		location.pathname.includes('/powerBI/userActivity/reports') ? 1 :
		location.pathname.includes('/powerBI/userActivity/users') ? 2 : 0
	);

	useEffect(() => {
		if (location.pathname.includes('/powerBI/userActivity/reports')) {
			setTabValue(1);
		} else if (location.pathname.includes('/powerBI/userActivity/users')) {
			setTabValue(2);
		} else {
			setTabValue(0);
		}
	}, [location.pathname]);
	const [userGrowthData, setUserGrowthData] = useState<any>({});
	const [activityTrendData, setActivityTrendData] = useState<any>({});
	const [tableData, setTableData] = useState<any[]>([]);
	const [totalUsers, setTotalUsers] = useState<number>(0);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: '' });
	const [filterInfo, setFilterInfo] = useState<{ [key: string]: any }>({});
	const [searchDetails] = useState<any[]>([]);
	const [pageSize, setPageSize] = useState<number>(100);
	const [filterDataOptions, setFilterDataOptions] = useState<any>(null);
	const [searchAfterMap, setSearchAfterMap] = useState(new Map());
	const [allSectors, setAllSectors] = useState<string[]>([]);
	const [sectorsLoaded, setSectorsLoaded] = useState<boolean>(false);

	useEffect(() => {
		if (
			location.pathname === '/powerBI/userActivity' ||
			location.pathname === '/powerBI/userActivity/'
		) {
			navigate('/powerBI/userActivity/dashboard', {
				replace: true
			});
		}
	}, [location.pathname, navigate]);

	// Filter states
	const [timeRange, setTimeRange] = useState<string>('TODAY'); // Updated to use PowerBI format
	const [dateRangeType, setDateRangeType] = useState<string>('today');
	const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
	const [customEndDate, setCustomEndDate] = useState<Date | null>(null);

	// Sector filter using ref (like CapacityObservability)
	const checkedListRef = useRef<any>([]);

	// KPI data states
	const [kpiData, setKpiData] = useState<any>(null);
	const [kpiLoading, setKpiLoading] = useState<boolean>(true);

	// Chart data states
	const [chartTimeRange, setChartTimeRange] = useState<string>('Monthly');

	// Loading states for individual components
	const [growthLoading, setGrowthLoading] = useState<boolean>(true);
	const [trendLoading, setTrendLoading] = useState<boolean>(true);
	const [tableLoading, setTableLoading] = useState<boolean>(false);

	// User status filter state for KPI card interactions
	const [userStatusFilter, setUserStatusFilter] = useState<string | undefined | null>('active');
	const [selectedTitle, setSelectedTitle] = useState<string>('');
	const [sectorsChange, setSectorsChange] = useState<string>('');
	const afterKeyRef = useRef<any>(null);
	const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);

	// Table data states
	// const [tableLoading, setTableLoading] = useState<boolean>(false);

	// Fetch KPI data
	const updateAfterKey = (value: any) => {
		afterKeyRef.current = value;
	};

	const handleDateRangeChange = (type: string, start?: Date, end?: Date) => {
		setDateRangeType(type);
		setCustomStartDate(type === 'custom' ? start ?? null : null);
		setCustomEndDate(type === 'custom' ? end ?? null : null);
		updateAfterKey(null);
		const selectedTimeRange = POWERBI_DATE_RANGE_MAP[type];
		if (selectedTimeRange) setTimeRange(selectedTimeRange);
	};

	const fetchFilterOptions = async () => {
		try {
			let sectors = await getPepwiseSectorList();
			const filterSectors = sectors?.data?.sectors.map((s: any) => s.Sector_Name) || [];

			// Fetch initial users list
			const usersResponse = await fetchUserActivityUsersFilter('');
			const users = usersResponse?.data || [];

            // Fetch other filter options with current userStatusFilter
            const reportNames = await fetchUserActivityFilterOptions('reportName', '', userStatusFilter || undefined);
            const workspaceNames = await fetchUserActivityFilterOptions('workspaceName', '', userStatusFilter || undefined);
            const capacityNames = await fetchUserActivityFilterOptions('capacityName', '', userStatusFilter || undefined);
            const userTypes = await fetchUserActivityFilterOptions('userType', '', userStatusFilter || undefined);
           
			if(userStatusFilter === "active")
            {
            setFilterDataOptions({
                sector: filterSectors,
                userType: userTypes?.data || ['Creator', 'Viewer'],
                activityStatus: ['active', 'inactive'],
                user: users,
                reportName: reportNames?.data || [],
                workspaceName: workspaceNames?.data || [],
                capacityName: capacityNames?.data || [],
                multiSectorAccess: ['Yes', 'No']
            });
        }
        if(userStatusFilter === "inactive"){
             setFilterDataOptions({
                sector: filterSectors,
                activityStatus: ['active', 'inactive'],
                user: users,
                workspaceName: workspaceNames?.data || [],
                capacityName: capacityNames?.data || [],
            });
        }
        } catch (error) {
            console.error('Error fetching filter options:', error);
        }
    };


	const fetchKPIData = useCallback(async () => {

		if (!sectorsLoaded) return;

		try {
			setKpiLoading(true);
			const selectedSectors = checkedListRef?.current?.length > 0 
				  ? checkedListRef?.current?.join(',') 
				  : allSectors.join(',');

			const response = await fetchPowerBIKPIData(selectedSectors, timeRange);

			if (response?.success && response?.data) {
				setKpiData(response.data.data);
			} else {
				console.error('Invalid KPI response structure:', response);
			}
		} catch (error) {
			console.error('Error fetching KPI data:', error);
		} finally {
			setKpiLoading(false);
		}
    }, [allSectors, timeRange, sectorsLoaded]);

	// Fetch Growth Chart Data
	const fetchGrowthData = useCallback(async () => {
		if (!sectorsLoaded) return;
		try {
			setGrowthLoading(true);
			const selectedSectors = checkedListRef?.current?.length > 0 
				  ? checkedListRef?.current?.join(',') 
				  : allSectors.join(',');

			const response = await fetchUserActivityGrowthData(selectedSectors, timeRange, chartTimeRange);

			if (response?.success && response?.data) {
				// Handle potential double nesting from backend
				const responseData = response.data.data ? response.data.data : response.data;
				setUserGrowthData(responseData);
			} else {
				console.error('Invalid Growth response structure:', response);
			}
		} catch (error) {
			console.error('Error fetching Growth data:', error);
		} finally {
			setGrowthLoading(false);
		}
	}, [timeRange, chartTimeRange, allSectors, sectorsLoaded]);

	// Fetch Trend Chart Data
	const fetchTrendData = useCallback(async () => {
		if (!sectorsLoaded) return;
		try {
			setTrendLoading(true);
			const selectedSectors = checkedListRef?.current?.length > 0 
				  ? checkedListRef?.current?.join(',') 
				  : allSectors.join(',');

			const response = await fetchUserActivityTrendData(selectedSectors, timeRange);

			if (response?.success && response?.data) {
				setActivityTrendData(response.data.data);
			} else {
				console.error('Invalid Trend response structure:', response);
			}
		} catch (error) {
			console.error('Error fetching Trend data:', error);
		} finally {
			setTrendLoading(false);
		}
	}, [timeRange, allSectors, sectorsLoaded]);

	// Load next page of data


	// Fetch Table Data with Composite Aggregation

	const fetchTableData = useCallback(async (page: any, isPageJump = false, isLoadingMore = false) => {
		try {
			// Set loading state
			if (isLoadingMore) {
				setIsLoadingMore(true);
			} else {
				setTableLoading(true);
			}

			// Get selected sectors
			const selectedSectors = getSelectedSectors(checkedListRef.current, allSectors);

			// Get search after value
			const searchAfterValue = getSearchAfterValue(
				isPageJump,
				page,
				userStatusFilter,
				searchAfterMap
			);

			// Build request body
			const requestBody = buildTableDataRequest({
				selectedSectors,
				timeRange,
				userStatusFilter,
				page,
				pageSize,
				sortingInfo,
				filterInfo,
				searchDetails,
				searchAfterValue,
				isPageJump
			});

			console.log('Fetching table data:', {
				page,
				isPageJump,
				searchAfter: searchAfterValue ? 'provided' : 'null',
				userStatusFilter
			});

			// Fetch data
			const response = await fetchUserActivityTableData(requestBody);

			// Process response
			if (response?.success && response?.data) {
				const { users, totalUsers, page: pageInfo, isReverseSorted } = response.data;

				// Update table data using appropriate method
				if (isLoadingMore) {
					appendTableData(users, tableData, setTableData);
				} else {
					replaceTableData(users, setTableData);
				}
				
				// Update other state
				setTotalUsers(totalUsers);
				setCurrentPage(page);

				// Handle search after storage
				handleSearchAfterStorage(
					isReverseSorted,
					pageInfo,
					userStatusFilter,
					page,
					setSearchAfterMap
				);
			}
		} catch (error) {
			console.error('Error fetching Table data:', error);
		} finally {
			setTableLoading(false);
			setIsLoadingMore(false);
		}
	}, [timeRange, userStatusFilter, pageSize, sortingInfo, filterInfo, searchDetails, searchAfterMap, allSectors, tableData]);

    useEffect(() => {
        fetchFilterOptions();
    }, [userStatusFilter]);

	console.log('outside current page', currentPage)

	const defaultColumnNames = [
			'User',
			'Sector',
			'User Type',
			'Last Login (UTC)',
			'Report Name',
			'Workspace Name',
			'Capacity Name',
			'Multisector',
			'Status'
	];

    // Export Table Data
    const exportGetUserActivityDetails = async () => {
        let exportColumns = defaultColumnNames
        let formatType = "EXCEL"
        let tableName = ""
       const normalizeSector = (sector: string) =>
            sector.toLowerCase() === 'europe' ? 'Europe' : sector;

        let selectedSectors: string[] = [];

        if (checkedListRef.current.length > 0 && checkedListRef.current[0].toLowerCase() !== 'all') {
            selectedSectors = checkedListRef.current.map(normalizeSector);
        } else {
            selectedSectors = allSectors.map(normalizeSector);
        }


        const requestBody = {
            sector: selectedSectors,
            filterOption: timeRange,
            userStatusFilter: userStatusFilter,
            sortBy: sortingInfo.sortBy,
            orderBy: sortingInfo.sortDir,
            filterBy: filterInfo,
            exportColumns: exportColumns,
            formatType: formatType,
            tableName: tableName,
        };
        try{
			const res = await exportUserActivityDetails(requestBody);
			console.log(res)
		}
		catch (error)
		{
			console.error(error);
			handleExportError(error);
		}
	};

	useEffect(() => {
		const fetchAllSectors = async () => {
			try {
				const response = await getPepwiseSectorList();
				if (response?.success && response?.data?.sectors) {
					const sectorNames = response.data.sectors
						.map((sector: any) => sector.Sector_Name)
						.filter((name: string) => name !== 'Unknown');
					setAllSectors(sectorNames);
					setSectorsLoaded(true);
				}
			} catch (error) {
				console.error('Error fetching sectors:', error);
				setSectorsLoaded(true);
			}
		};
	
		fetchAllSectors();
	}, []); 

	// Reset pagination when filters change
	useEffect(() => {
		setSearchAfterMap(new Map());
		setCurrentPage(1);
		setTableData([]);
	}, [timeRange, userStatusFilter, sortingInfo, filterInfo, searchDetails]);

	// Handle initial load and filter changes
	useEffect(() => {
		if (tabValue === 2) {
			fetchTableData(1, false, false);
		}
	}, [timeRange, userStatusFilter, sortingInfo, filterInfo, tabValue, pageSize]);

	const fetchData = useCallback(async () => {
		if (!sectorsLoaded) return;
		try {
			// Initialize states properly - these will be populated by individual API calls
			setUserGrowthData({}); // Initialize as object for growth data
			setActivityTrendData({}); // Initialize as object for trend data  
			setTableData([]); // Keep as array for table data
			setTotalUsers(0);
		} catch (error) {
			console.error('Error fetching data:', error);
		} finally { /* empty */ }
	}, [allSectors, currentPage, sectorsLoaded]);

	// Initial data fetch on component mount
	useEffect(() => {
		if (!sectorsLoaded) return;
		fetchData();
		// Only fetch Users-tab APIs when Users tab is active
		if (tabValue === 2) {
			fetchKPIData();
			fetchGrowthData();
			fetchTrendData();
			fetchTableData(1, false, false);
		}
	}, [allSectors, sectorsLoaded]); // Only run on mount

	// When timeRange changes, only refetch if Users tab is active
	useEffect(() => {
		if (!sectorsLoaded) return;
		if (tabValue === 2) {
			fetchKPIData();
			fetchGrowthData();
			fetchTrendData();
			fetchTableData(1, false, false);
		}
	}, [timeRange, tabValue, allSectors, sectorsLoaded]);

	// Effect for when user status filter changes (KPI card clicks)
	useEffect(() => {
		if (tabValue === 2) {
			fetchTableData(1, false, false);
		}
	}, [userStatusFilter, tabValue]);

	const handlePageChange = (event: any, newPage: any) => {
    const isSequential = Math.abs(newPage - currentPage) === 1;
    const isLargeJump = Math.abs(newPage - currentPage) > 5; // Consider jumps > 5 pages as large jumps
    
	// Treat all non-sequential clicks AND large jumps as page jumps
    const isPageJump = !isSequential || isLargeJump;

    console.log(`Page change - From: ${currentPage}, To: ${newPage}, Sequential: ${isSequential}, LargeJump: ${isLargeJump}, IsPageJump: ${isPageJump}`);

    fetchTableData(newPage, isPageJump, false);
};
	const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
		const tabRoutes = [
			'/powerBI/userActivity/dashboard',
			'/powerBI/userActivity/reports',
			'/powerBI/userActivity/users'
		];
		setTabValue(newValue);
		navigate(tabRoutes[newValue]);
		// On switching to Users tab, ensure the Users APIs are loaded fresh
		if (newValue === 2) {
			fetchKPIData();
			fetchGrowthData();
			fetchTrendData();
			fetchTableData(1, false, false);
		}
	};

	// Handle sector filter OK click (like CapacityObservability)
	const handleOkClick = () => {
		updateAfterKey(null);
		// Refresh only Users tab data when Users tab is selected
		if (tabValue === 2) {
			fetchKPIData();
			fetchGrowthData();
			fetchTrendData();
			fetchTableData(1, false, false);
		}
		const newSectorChange = 'sectors-' + Date.now();
		setSectorsChange(newSectorChange);
	};

	// Handle chart time period change (like CapacityObservability)
	const handleTimePeriodChange = (event: { target: { value: string } }) => {
		const newTimePeriod: string = event.target.value;
		setChartTimeRange(newTimePeriod);
		// Only refresh chart data with new time period - don't refetch other components
		// Use setTimeout to ensure state is updated before API call
		setTimeout(() => {
			const rawSectors = checkedListRef.current.length > 0 
            ? checkedListRef.current 
            : allSectors;

			// Expand "AMESA/APAC" into ["AMESA", "APAC"]
			const expandedSectors = rawSectors.flatMap((sector:any) =>
				sector === 'AMESA/APAC' ? ['AMESA', 'APAC'] : [sector]
			);

			// Join into comma-separated string
			const selectedSectors = expandedSectors.join(',');
			setGrowthLoading(true);
			fetchUserActivityGrowthData(selectedSectors, timeRange, newTimePeriod)
				.then((response) => {
					if (response?.success && response?.data) {
						// Handle potential double nesting from backend
						const responseData = response.data.data ? response.data.data : response.data;
						setUserGrowthData(responseData);
					}
				})
				.catch((error) => {
					console.error('Error fetching Growth data:', error);
				})
				.finally(() => {
					setGrowthLoading(false);
				});
		}, 0);
	};

	// Handle KPI card clicks to filter table
	const handleKPICardClick = (filterType: string, cardTitle: string) => {
		console.log('KPI Card clicked:', filterType, cardTitle, selectedTitle);

		// If clicking the same card, clear all filters
		if (selectedTitle === cardTitle) {
			setSelectedTitle('');
			setFilterInfo({}); // Clear all filter info
		} else {
			// Set new filter based on card type
			// setUserStatusFilter(filterType);
			setSelectedTitle(cardTitle);

			// Set specific filter criteria based on card type
			let newFilterInfo: { [key: string]: any } = {};

			switch (filterType) {
				case 'active':
					setUserStatusFilter("active");
					break;
				case 'inactive':
					setUserStatusFilter("inactive");
					break;
				case 'creators':
					setUserStatusFilter("active");
					newFilterInfo = { userType: ['Creator'] };
					break;
				case 'viewers':
					setUserStatusFilter("active");
					newFilterInfo = { userType: ['Viewer'] };
					break;
				case 'multiSector':
					setUserStatusFilter("active");
					newFilterInfo = { multiSectorAccess: ['Yes'] };
					break;
				default:
					newFilterInfo = {};
			}

			setFilterInfo(newFilterInfo);
		}
	};

	// Helper function for KPI formatting
	const formatNumber = (num: number): string => {
		if (num >= 1000000) {
			return (num / 1000000).toFixed(1) + 'M';
		} else if (num >= 1000) {
			return (num / 1000).toFixed(1) + 'k';
		}
		return num.toString();
	};

	// Inline KPI Tiles Component using NumberWidget
	const renderKPITiles = () => {
		return (
			<Grid container spacing={1}>
				{/* First Row - 2 tiles */}
				<Grid item xs={12} sm={6} id="active-users">
					<Box sx={{ position: 'relative', height:'100%' }}>
						<NumberWidget
							isLoading={kpiLoading}
							title={<HighlightedText text="Active Users" />}
							titleKey="Active Users"
							tooltipLabel="Total number of users who performed any Power BI activity within the selected date range"
							hasTooltip={true}
							number={kpiData?.activeUsers?.value ? formatNumber(kpiData?.activeUsers?.value) : 0}
							change={kpiData?.activeUsers?.change || 0}
							changeType={kpiData?.activeUsers?.changeType || 'increase'}
							bgColor={theme.palette?.widget?.background?.card}
							color={theme.palette?.widget?.text?.main}
							valueFontWeight={300}
							valueFontSize={{ xs: '24px', sm: '32px', md: '42px' }}
							sameDecimalSize={true}
							selectedTitle={selectedTitle}
							showChangePercentage={true}
							containerProps={{
								minHeight: '140px',
								sx: {
									flexDirection: 'column',
									alignItems: 'flex-start',
									cursor: 'pointer',
									'&:hover': {
										boxShadow: `0 4px 12px ${theme.palette?.widget?.chart?.emptyCircleFill}`,
										transform: 'translateY(-2px)',
										transition: 'all 0.2s ease-in-out'
									}
								},
								onClick: () => handleKPICardClick('active', 'Active Users')
							}}
						/>
					</Box>
				</Grid>
				<Grid item xs={12} sm={6} id="inactive-users">
					<Box sx={{ position: 'relative', height:'100%' }}>
						<NumberWidget
							isLoading={kpiLoading}
							title={<HighlightedText text="Inactive Users" />}
							titleKey="Inactive Users"
							tooltipLabel="Total number of users who did not perform any Power BI activity within the selected date range"
							hasTooltip={true}
							number={kpiData?.inactiveUsers?.value ? formatNumber(kpiData?.inactiveUsers?.value) : 0}
							change={kpiData?.inactiveUsers?.change || 0}
							changeType={kpiData?.inactiveUsers?.changeType || 'decrease'}
							bgColor={theme.palette?.widget?.background?.card}
							color={theme.palette?.widget?.text?.main}
							valueFontWeight={300}
							valueFontSize={{ xs: '24px', sm: '32px', md: '42px' }}
							sameDecimalSize={true}
							selectedTitle={selectedTitle}
							showChangePercentage={true}
							containerProps={{
								minHeight: '140px',
								sx: {
									flexDirection: 'column',
									alignItems: 'flex-start',
									cursor: 'pointer',
									'&:hover': {
										boxShadow: `0 4px 12px ${theme.palette?.widget?.chart?.emptyCircleFill}`,
										transform: 'translateY(-2px)',
										transition: 'all 0.2s ease-in-out'
									}
								},
								onClick: () => handleKPICardClick('inactive', 'Inactive Users')
							}}
						/>
					</Box>
				</Grid>

				{/* Second Row - 3 tiles */}
				<Grid item xs={12} sm={4} id="creators">
					{/* <Grid item xs={12} sm={4}> */}
                    <Box sx={{ position: 'relative', height: '100%' }}>
						<NumberWidget
							isLoading={kpiLoading}
							title={<HighlightedText text="Creators" />}
							titleKey="Creators"
							tooltipLabel="Total number of users who performed Power BI activities with an activity type starting with “Create” (e.g., CreateReport, CreateDashboard) within the selected date range."
							hasTooltip={true}
							number={kpiData?.dailyCreators?.value ? formatNumber(kpiData?.dailyCreators?.value) : 0}
							change={kpiData?.dailyCreators?.change || 0}
							changeType={kpiData?.dailyCreators?.changeType || 'increase'}
							bgColor={theme.palette?.widget?.background?.card}
							color={theme.palette?.widget?.text?.main}
							valueFontWeight={300}
							valueFontSize={{ xs: '24px', sm: '32px', md: '42px' }}
							sameDecimalSize={true}
							selectedTitle={selectedTitle}
							showChangePercentage={true}
							containerProps={{
								minHeight: '140px',
								sx: {
									flexDirection: 'column',
									alignItems: 'flex-start',
									cursor: 'pointer',
									'&:hover': {
										boxShadow: `0 4px 12px ${theme.palette?.widget?.chart?.emptyCircleFill}`,
										transform: 'translateY(-2px)',
										transition: 'all 0.2s ease-in-out'
									}
								},
								onClick: () => handleKPICardClick('creators', 'Creators')
							}}
						/>
					</Box>

				</Grid>
				<Grid item xs={12} sm={4} id="viewers">
					{/* <Grid item xs={12} sm={4}> */}
                    <Box sx={{ position: 'relative', height:'100%' }}>
						<NumberWidget
							isLoading={kpiLoading}
							title={<HighlightedText text="Viewers" />}
							titleKey="Viewers"
							tooltipLabel="Total number of users who performed Power BI activities with an activity type starting with “View” (e.g., ViewReport, ViewDashboard) within the selected date range."
							hasTooltip={true}
							number={kpiData?.dailyViewers?.value ? formatNumber(kpiData?.dailyViewers?.value) : 0}
							change={kpiData?.dailyViewers?.change || 0}
							changeType={kpiData?.dailyViewers?.changeType || 'increase'}
							bgColor={theme.palette?.widget?.background?.card}
							color={theme.palette?.widget?.text?.main}
							valueFontWeight={300}
							valueFontSize={{ xs: '24px', sm: '32px', md: '42px' }}
							sameDecimalSize={true}
							selectedTitle={selectedTitle}
							showChangePercentage={true}
							containerProps={{
								minHeight: '140px',
								sx: {
									flexDirection: 'column',
									alignItems: 'flex-start',
									cursor: 'pointer',
									'&:hover': {
										boxShadow: `0 4px 12px ${theme.palette?.widget?.chart?.emptyCircleFill}`,
										transform: 'translateY(-2px)',
										transition: 'all 0.2s ease-in-out'
									}
								},
								onClick: () => handleKPICardClick('viewers', 'Viewers')
							}}
						/>
					</Box>
				</Grid>
				<Grid item xs={12} sm={4}>
					<Box sx={{ position: 'relative', height:'100%' }}>
						<NumberWidget
							isLoading={kpiLoading}
							title={<HighlightedText text="Cross-Sector Report Users" />}
							titleKey="Cross-Sector Report Users"
							tooltipLabel="Total number of users who accessed Power BI reports across at least two distinct sectors within the selected date range."
							hasTooltip={true}
							number={kpiData?.multiSectorUsers?.value ? formatNumber(kpiData?.multiSectorUsers?.value) : 0}
							change={kpiData?.multiSectorUsers?.change || 0}
							changeType={kpiData?.multiSectorUsers?.changeType || 'increase'}
							bgColor={theme.palette?.widget?.background?.card}
							color={theme.palette?.widget?.text?.main}
							valueFontWeight={300}
							valueFontSize={{ xs: '24px', sm: '32px', md: '42px' }}
							sameDecimalSize={true}
							selectedTitle={selectedTitle}
							showChangePercentage={true}
							containerProps={{
								minHeight: '140px',
								sx: {
									flexDirection: 'column',
									alignItems: 'flex-start',
									cursor: 'pointer',
									'&:hover': {
										boxShadow: `0 4px 12px ${theme.palette?.widget?.chart?.emptyCircleFill}`,
										transform: 'translateY(-2px)',
										transition: 'all 0.2s ease-in-out'
									}
								},
								onClick: () => handleKPICardClick('multiSector', 'Cross-Sector Report Users')
							}}
						/>
					</Box>
				</Grid>
			</Grid>
		);
	};

	// Inline User Growth Chart Component
	const renderUserGrowthChart = () => {
		// Use API data or fallback to empty array
		const rawData = userGrowthData?.barChartData || [];
		const availableSectors = userGrowthData?.sectors || [];

		// Debug logs (can be removed in production)
		// console.log('UserGrowthData:', userGrowthData);
		// console.log('RawData:', rawData);
		// console.log('AvailableSectors:', availableSectors);

		// Transform API data to match BarChartWidget format
		let chartData: any[] = [];
		let seriesData: any[] = [];

		if (rawData.length > 0 && availableSectors.length > 0) {
			// Generate dynamic colors for all available sectors
			const sectorColorMap = generateSectorColors(availableSectors);

			seriesData = availableSectors.map((sector: string) => ({
				name: sector,
				dataKey: sector,
				color: sectorColorMap[sector]
			}));

			// Create custom ECharts option
			const customChartOption: any = {
				backgroundColor: theme.palette?.widget?.background?.chart,
				tooltip: {
					trigger: 'axis',
					backgroundColor: theme.palette?.widget?.chart?.tooltipBg,
					borderColor: theme.palette?.widget?.chart?.legendTextColor,
					borderWidth: 1,
					textStyle: {
						color: theme.palette?.widget?.chart?.tooltipText,
						fontSize: 12
					},
					axisPointer: {
						type: 'shadow',
						shadowStyle: {
							color: theme.palette?.widget?.chart?.emptyCircleFill
						}
					},
					formatter: function (params: any) {
						let tooltipContent = `<div style="font-weight: bold; margin-bottom: 8px;">${params[0].name}</div>`;
						let totalUsers = 0;

						params.forEach((param: any) => {
							if (param.value > 0) {
								totalUsers += param.value;
								const formattedValue = formatValue(param.value);
								tooltipContent += `
									<div style="display: flex; align-items: center; margin-bottom: 4px;">
										<span style="display: inline-block; width: 10px; height: 10px; background-color: ${param.color}; margin-right: 8px; border-radius: 2px;"></span>
										<span style="margin-right: 8px;">${param.seriesName}:</span>
										<span style="font-weight: bold;">${formattedValue}</span>
									</div>
								`;
							}
						});

						const formattedTotal = formatValue(totalUsers);
						tooltipContent += `<div style="border-top: 1px solid ${theme.palette?.widget?.chart?.legendTextColor}; padding-top: 6px; margin-top: 6px; font-weight: bold;">Total: ${formattedTotal}</div>`;

						return tooltipContent;
					}
				},
				legend: {
					show: true,
					type: 'scroll',
					orient: 'horizontal',
					right: '20px',
					bottom: '20px',
					itemWidth: 14,
					itemHeight: 14,
					itemGap: 15,
					textStyle: {
						color: theme.palette?.widget?.chart?.legendTextColor,
						fontSize: 11
					},
					pageIconColor: theme.palette?.widget?.chart?.legendActiveUpDownIconColor,
					pageIconInactiveColor: theme.palette?.widget?.chart?.legendInactiveUpDownIconColor,
					pageTextStyle: {
						color: theme.palette?.widget?.chart?.legendTextColor,
						fontSize: 10
					},
					data: seriesData.map(s => s.name)
				},
				grid: {
					left: '3%',
					right: '4%',
					bottom: '20%',
					top: '10%',
					containLabel: true
				},
				xAxis: {
					type: 'category' as const,
					data: rawData.map((item: any) => item.period),
					axisLabel: {
						color: theme.palette?.widget?.chart?.labelColorSecondary,
						fontSize: 11,
						interval: 0,
						rotate: 0
					},
					axisLine: {
						show: true,
						lineStyle: {
							color: theme.palette?.widget?.chart?.legendTextColor
						}
					},
					axisTick: {
						show: false
					},
					splitLine: {
						show: false
					}
				},
				yAxis: {
					type: 'value' as const,
					axisLabel: {
						color: theme.palette?.widget?.chart?.labelColorSecondary,
						fontSize: 11,
						formatter: formatValue
					},
					axisLine: {
						show: false
					},
					axisTick: {
						show: false
					},
					splitLine: {
						show: true,
						lineStyle: {
							type: 'dashed',
							color: theme.palette?.widget?.chart?.emptyCircleFill,
							width: 1
						}
					}
				},
				series: seriesData.map((series: any, index: number) => ({
					name: series.name,
					type: 'bar' as const,
					stack: 'total',
					data: rawData.map((item: any) => item[series.dataKey] || 0),
					itemStyle: {
						color: series.color
					},
					emphasis: {
						focus: 'series'
					},
					// Add label only for the last series (top of the stack)
					label: index === seriesData.length - 1 ? {
						show: true,
						position: 'top',
						formatter: createLabelFormatter(rawData, availableSectors),
						color: theme.palette?.widget?.chart?.labelColorSecondary,
						fontSize: 11,
						fontWeight: 'bold'
					} : undefined
				}))
			};

			return (
				<Stack
					p={2}
					bgcolor={theme.palette?.widget?.background?.chart}
					fontFamily="Montserrat"
					borderRadius="5px"
					flexGrow={1}
					minHeight='calc(100% - 32px)'
					height='100%'
					maxHeight='320px'
				>
					<Stack direction="row" gap={1} justifyContent="space-between" alignItems="center" pb={1} id="user-growth-sector">
						<Typography variant="widgetTitle" sx={{ color: theme.palette?.widget?.text?.title }}><HighlightedText text="User Growth by Sector" /></Typography>
						<Box display='flex' gap={1}>
							<CustomDropdown
								handleChange={handleTimePeriodChange}
								value={chartTimeRange}
								options={[
									{ label: 'Monthly', value: 'Monthly' },
									{ label: 'Weekly', value: 'Weekly' }
								]}
								selectedTextColor={theme.palette?.DropdownV2?.dropdownIconDefault}
								textColor={theme.palette?.DropdownV2?.menuItemText}
								iconColor={theme.palette?.DropdownV2?.dropdownIconDefault}
								bgColor={theme.palette?.DropdownV2?.menuItemBackground}
								borderColor="transparent"
								hoverBgColor={theme.palette?.DropdownV2?.menuItemHover}
								hoverTextColor={theme.palette?.DropdownV2?.menuItemHoverText}
								selectedBgColor={theme.palette?.DropdownV2?.menuItemSelectedBg}
								boxProps={{
									sx: {
										backgroundColor: 'transparent !important',
										'& .MuiOutlinedInput-root': {
											backgroundColor: 'transparent !important'
										},
										'& .MuiSelect-select': {
											fontFamily: 'Montserrat',
											fontSize: '14px',
											fontWeight: 500,
											padding: '0 26px 0 0 !important',
											minHeight: '28px !important',
											display: 'flex',
											alignItems: 'center'
										},
										'& .MuiOutlinedInput-notchedOutline': {
											border: 'none !important'
										},
										'& .MuiSelect-icon': {
											color: `${theme.palette?.DropdownV2?.dropdownIconDefault} !important`,
											right: 0
										}
									}
								}}
							/>
						</Box>
					</Stack>
					<Stack height="280px" flexGrow={1} justifyContent="center">
						{growthLoading ? (
							<LoaderComponent />
						) : (
							<ReactECharts
								option={customChartOption}
								style={{ width: '100%', height: '100%' }}
								opts={{ renderer: 'canvas', width: 'auto', height: 'auto' }}
								notMerge={true}
								lazyUpdate={true}
							/>
						)}
					</Stack>
				</Stack>
			);
		} else {
			// Fallback when no data
			return (
				<Stack
					p={2}
					bgcolor={theme.palette?.widget?.background?.chart}
					fontFamily="Montserrat"
					borderRadius="5px"
					flexGrow={1}
					minHeight='calc(100% - 32px)'
					height='100%'
					maxHeight='320px'
				>
					<Stack direction="row" gap={1} justifyContent="space-between" alignItems="center" pb={1}>
						<Typography variant="widgetTitle" sx={{ color: theme.palette?.widget?.text?.title }}><HighlightedText text="User Growth by Sector" /></Typography>
						<Box display='flex' gap={1}>
							<CustomDropdown
								handleChange={handleTimePeriodChange}
								value={chartTimeRange}
								options={[
									{ label: 'Monthly', value: 'Monthly' },
									{ label: 'Weekly', value: 'Weekly' }
								]}
								selectedTextColor={theme.palette?.DropdownV2?.dropdownIconDefault}
								textColor={theme.palette?.DropdownV2?.menuItemText}
								iconColor={theme.palette?.DropdownV2?.dropdownIconDefault}
								bgColor={theme.palette?.DropdownV2?.menuItemBackground}
								borderColor="transparent"
								hoverBgColor={theme.palette?.DropdownV2?.menuItemHover}
								hoverTextColor={theme.palette?.DropdownV2?.menuItemHoverText}
								selectedBgColor={theme.palette?.DropdownV2?.menuItemSelectedBg}
								boxProps={{
									sx: {
										backgroundColor: 'transparent !important',
										'& .MuiOutlinedInput-root': {
											backgroundColor: 'transparent !important'
										},
										'& .MuiSelect-select': {
											fontFamily: 'Montserrat',
											fontSize: '14px',
											fontWeight: 500,
											padding: '0 26px 0 0 !important',
											minHeight: '28px !important',
											display: 'flex',
											alignItems: 'center'
										},
										'& .MuiOutlinedInput-notchedOutline': {
											border: 'none !important'
										},
										'& .MuiSelect-icon': {
											color: `${theme.palette?.DropdownV2?.dropdownIconDefault} !important`,
											right: 0
										}
									}
								}}
							/>
						</Box>
					</Stack>
					<Stack height="280px" flexGrow={1} justifyContent="center">
						<LoaderComponent />
					</Stack>
				</Stack>
			);
		}
	};


	// Inline Activity Trend Chart Component
	const renderActivityTrendChart = () => {
		// Use API data or fallback to hardcoded data
		const rawData = activityTrendData?.trendData || [];

		// Get all daily points from all months and flatten them
		let allDailyPoints: any[] = [];
		let xAxisLabels: string[] = [];
		let activeUsersData: number[] = [];
		let inactiveUsersData: number[] = [];
		let monthBreakpoints: { index: number; monthLabel: string }[] = [];

		if (rawData.length > 0) {
			// Flatten all daily points from all months and track month breakpoints
			rawData.forEach((monthData: any, monthIndex: number) => {
				if (monthData.dailyPoints && monthData.dailyPoints.length > 0) {
					// Mark the start of each month
					monthBreakpoints.push({
						index: allDailyPoints.length,
						monthLabel: monthData.month // e.g., "Jun 25"
					});

					allDailyPoints = [...allDailyPoints, ...monthData.dailyPoints];
				}
			});

			// Sort by date to ensure chronological order
			allDailyPoints.sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());

			// Extract data for chart
			allDailyPoints.forEach((point: any) => {
				xAxisLabels.push(point.hoverDate); // Using hoverDate for display
				activeUsersData.push(point.activeUsers || 0);
				inactiveUsersData.push(point.inactiveUsers || 0);
			});
		}

		// If no data, create empty arrays
		if (allDailyPoints.length === 0) {
			xAxisLabels = new Array(30).fill('').map((_, i: number) => `${i + 1}`);
			activeUsersData = new Array(30).fill(0);
			inactiveUsersData = new Array(30).fill(0);
		}

		// ECharts options for multi-line chart with daily data but month labels
		const inactiveUsersColor = theme.palette?.boxColor?.red?.bgColor;
		const activeUsersColor = theme.palette?.boxColor?.blue?.color;
		const chartOptions = {
			backgroundColor: theme.palette?.widget?.background?.chart,
			tooltip: {
				trigger: 'axis',
				backgroundColor: theme.palette?.widget?.chart?.tooltipBg,
				borderColor: theme.palette?.widget?.chart?.legendTextColor,
				borderWidth: 1,
				textStyle: {
					color: theme.palette?.widget?.chart?.tooltipText,
					fontSize: 12
				},
				axisPointer: {
					type: 'cross',
					label: {
						backgroundColor: theme.palette?.widget?.chart?.legendTextColor
					}
				},
				formatter: function (params: any) {
					const dataIndex = params[0].dataIndex;
					const rawDate = allDailyPoints[dataIndex]?.dateString;

					let formattedDate = "Unknown date";

					if (rawDate) {
						const parsedDate = new Date(rawDate);
						if (!Number.isNaN(parsedDate.getTime())) {
							formattedDate = new Intl.DateTimeFormat('en-GB', {
								day: 'numeric',
								month: 'long',
								year: 'numeric'
							}).format(parsedDate);
						}
					}

					let tooltipContent = `<div style="margin-bottom: 5px; font-weight: bold;">${formattedDate}</div>`;

					params.forEach((param: any) => {
						const color = param.color;
						const seriesName = param.seriesName;
						const value = param.value.toLocaleString();
						tooltipContent += `
							<div style="display: flex; align-items: center; margin-bottom: 3px;">
								<span style="display: inline-block; width: 10px; height: 10px; background-color: ${color}; margin-right: 8px; border-radius: 2px;"></span>
								<span style="margin-right: 8px;">${seriesName}:</span>
								<span style="font-weight: bold;">${value}</span>
							</div>
						`;
					});

					return tooltipContent;
				}


			},
			legend: {
				data: ['Active Users', 'Inactive Users'],
				textStyle: {
					color: theme.palette?.widget?.chart?.legendTextColor,
					fontSize: 11
				},
				itemWidth: 14,
				itemHeight: 14,
				itemGap: 15,
				icon: 'roundRect',
				bottom: '0',
				left: 'left'
			},
			grid: {
				left: '3%',
				right: '4%',
				bottom: '15%',
				top: '10%',
				containLabel: true
			},
			xAxis: {
				type: 'category',
				boundaryGap: false,
				data: xAxisLabels,
				axisLabel: {
					color: theme.palette?.widget?.chart?.labelColorSecondary,
					interval: 0, // Show all labels first, then we'll customize
					rotate: 45,

					formatter: function (value: string, index: number) {
						const monthBreakpoint = monthBreakpoints.find(bp => bp.index === index);
						return monthBreakpoint ? monthBreakpoint.monthLabel : '';
					}
					
				},
				axisLine: {
					lineStyle: {
						color: theme.palette?.widget?.chart?.legendTextColor
					}
				},
				splitLine: {
					show: false
				}
			},
			yAxis: {
				type: 'value',
				axisLabel: {
					color: theme.palette?.widget?.chart?.labelColorSecondary
				},
				axisLine: {
					lineStyle: {
						color: theme.palette?.widget?.chart?.legendTextColor
					}
				},
				splitLine: {
					show: false
				}
			},
			series: [
				{
					name: 'Active Users',
					type: 'line',
					data: activeUsersData,
					smooth: true,
					symbol: 'circle',
					symbolSize: 6,
					lineStyle: {
						color: activeUsersColor,
						width: 3
					},
					itemStyle: {
						color: activeUsersColor
					}
				},
				{
					name: 'Inactive Users',
					type: 'line',
					data: inactiveUsersData,
					smooth: true,
					symbol: 'circle',
					symbolSize: 6,
					lineStyle: {
						color: inactiveUsersColor,
						width: 3
					},
					itemStyle: {
						color: inactiveUsersColor
					}
				}
			]
	
		};

		return (
			<Stack id="user-activity-trend">
				<MultiLineWidget
					isLoading={trendLoading}
					title={<HighlightedText text="User Activity Trend" />}
					data={chartOptions}
					containerColor={theme.palette?.widget?.background?.chart}
					titleColor={theme.palette?.widget?.text?.title}
				/>
			</Stack>
		);
	};

	// Inline User Activity Table Component
	const renderUserActivityTable = () => {
		const transformedData = tableData.map((item) => ({
            user: item.userName || '--',
            sector: item.sector || '--',
            userType: item.userType || '--',
            lastLogin: (item.lastLogin && item.lastLogin !== '--')
                ? (() => {
                    // Ensure the date string has 'Z' for UTC
                    const dateString = item.lastLogin.endsWith('Z')
                        ? item.lastLogin
                        : item.lastLogin + 'Z';

                    const d = new Date(dateString);

                    // Format as YYYY-MM-DD HHh:MMm
                    const year = d.getUTCFullYear();
                    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
                    const day = String(d.getUTCDate()).padStart(2, '0');
                    const hours = String(d.getUTCHours()).padStart(2, '0');
                    const minutes = String(d.getUTCMinutes()).padStart(2, '0');

                    return `${year}-${month}-${day} ${hours}h:${minutes}m`;
                })()
                : '--',
            multiSectorAccess: item.multiSectorAccess ? item.multiSectorAccess : 'No',
            activityStatus: item.activityStatus ? item.activityStatus.charAt(0).toUpperCase() + item.activityStatus.slice(1).toLowerCase() : '--',
            reportName: item?.reportName || "--",
            workspaceName: item?.workspaceName || "--",
            capacityName: item?.capacityName || "--"
        }));

		const getColumns = () => {
			return [
				{
					key: 'user',
					name: 'User',
					formatter: UserFormatter,
					minWidth: 180,
					resizable: true,
					flexGrow: 1
				},
				{
					key: 'sector',
					name: 'Sector',
					formatter: SectorFormatter,
					minWidth: 120,
					resizable: true,
					flexGrow: 1
				},
				{
					key: 'userType',
					name: 'User Type',
					formatter: UserTypeFormatter,
					minWidth: 120,
					resizable: true,
					flexGrow: 1
				},
				{
					key: 'lastLogin',
					name: 'Last Login (UTC)',
					formatter: LastLoginFormatter,
					minWidth: 280,
					resizable: true,
					flexGrow: 1
				},
				{
					key: 'reportName',
					name: 'Report Name',
					formatter: ReportNameFormatter,
					minWidth: 200,
					resizable: true,
					flexGrow: 1
				},
				{
					key: 'workspaceName',
					name: 'Workspace Name',
					formatter: WorkspaceNameFormatter,
					minWidth: 180,
					resizable: true,
					flexGrow: 1
				},
				{
					key: 'capacityName',
					name: 'Capacity Name',
					formatter: CapacityNameFormatter,
					minWidth: 180,
					resizable: true,
					flexGrow: 1
				},
				{
					key: 'multiSectorAccess',
					name: 'Multisector',
					formatter: MultiSectorAccessFormatter,
					minWidth: 150,
					resizable: true,
					flexGrow: 1
				},
				{
					key: 'activityStatus',
					name: 'Status',
					cellClass: 'center-text',
					formatter: StatusFormatter,
					minWidth: 100,
					resizable: true,
					flexGrow: 1
				}
			];
		};
		
		const paginationData = {
			currentPage: currentPage,
			totalPages: Math.ceil(totalUsers / pageSize),
			totalElements: totalUsers,
			pageSize: pageSize
		};

		const handleSorting = (sortingInfo: any) => {
			setSortingInfo(sortingInfo);
			setCurrentPage(1);
			setSearchAfterMap(new Map());
		};

        const handleFilter = (newFilterInfo: any) => {
		console.log("filterinfotime", newFilterInfo);
		const updatedFilterInfo = { ...filterInfo };
		
		// Process all filter keys
		Object.keys(newFilterInfo).forEach(key => {
			processFilterKey(key, newFilterInfo[key], updatedFilterInfo);
		});
		
		// Handle activity status change
		handleActivityStatusChange(
			newFilterInfo.activityStatus,
			setUserStatusFilter,
			setFilterInfo,
			refreshFilterOptions
		);
	
	console.log("Updated filter info:", updatedFilterInfo);
	setFilterInfo(updatedFilterInfo);
	setCurrentPage(1);
	setSearchAfterMap(new Map());
};


		const refreshFilterOptions = async (status: string) => {
			try {
				const fieldsToRefresh = ['reportName', 'workspaceName', 'capacityName', 'userType'];

				for (const field of fieldsToRefresh) {
					const response = await fetchUserActivityFilterOptions(field, '', status);
					const options = response?.data || [];

					setFilterDataOptions((prev: any) => ({
						...prev,
						[field]: options
					}));
				}
			} catch (error) {
				console.error('Error refreshing filter options:', error);
			}
		};

		const handlePageSizeChange = (event: any) => {
			const newPageSize = Number.parseInt(event.target.value);
			setPageSize(newPageSize);
			setCurrentPage(1);
			setSearchAfterMap(new Map());
		};

		const handleSearch = async (searchDetails: any, columnKey: any) => {
			if (searchDetails?.length >= 3) {
				try {
					let response;

					switch (columnKey) {
						case 'user':
							response = await fetchUserActivityUsersFilter(searchDetails);
							break;
						case 'sector':
						case 'reportName':
						case 'workspaceName':
						case 'capacityName':
						case 'userType':
							// Pass userStatusFilter to the API call
							response = await fetchUserActivityFilterOptions(columnKey, searchDetails, userStatusFilter || undefined);
							break;
						default:
							return;
					}

					const options = response?.data || [];

					setFilterDataOptions((prev: any) => ({
						...prev,
						[columnKey]: options
					}));

				} catch (error) {
					console.error('Error performing search:', error);
				}
			} else if (searchDetails?.length === 0) {
				// Clear search and reset to initial options
				if (['user', 'sector', 'reportName', 'workspaceName', 'capacityName', 'userType'].includes(columnKey)) {
					let response;
					if (columnKey === 'user') {
						response = await fetchUserActivityUsersFilter('');
					} else {
						// Pass userStatusFilter for clearing as well
						response = await fetchUserActivityFilterOptions(columnKey, '', userStatusFilter || undefined);
					}

					const initialOptions = response?.data || [];
					setFilterDataOptions((prev: any) => ({
						...prev,
						[columnKey]: initialOptions
					}));
				}
			}
		};

		useEffect(() => {
			setRightContextContent(
				<Box display="flex" alignItems="center" gap={2}>
					{/* Date Filter using DateTimeRangeFilter component */}
						<DateTimeRangeFilter
							dateRangeType={dateRangeType}
							customStartDate={customStartDate}
							customEndDate={customEndDate}
							onChange={handleDateRangeChange}
							timezone="CST"
							fixedOptions={POWERBI_DATE_FIXED_OPTIONS}
							showCustomRange={false}
						/>

						{/* Sector Filter using SectorCheckFilter component */}
						<Box sx={{ width: { xs: '100%', sm: 'auto' } }}> {/* Full width on mobile */}
							<SectorCheckFilter
								sectorStorageName="ml-summary-sector-filter"
								handleOkClick={handleOkClick}
								checkedListRef={checkedListRef}
								isNew={true}
							/>
						</Box>
				</Box>
			);

			return () => {
				setRightContextContent(null);
			};
		}, [dateRangeType, customStartDate, customEndDate]);
		return (
			<Box sx={{ width: '100%', height: 'auto', overflow: 'hidden' }} id="user-activity-details">
				<CustomizedDataGrid
					title={`User Activity Details - ${selectedTitle || 'Active Users'}`}
					tableColumns={getColumns()}
					tableData={transformedData}
					tableType="user-activity-details"
					defaultColumnNames={defaultColumnNames}
					onSorting={handleSorting}
					currSorting={{ sortBy: sortingInfo.sortBy, sortDir: sortingInfo.sortDir }}
					filterDataOptions={filterDataOptions}
					onFilterCheck={handleFilter}
					currFilter={filterInfo}
					searchDataValues={{}}
					onFilterSearch={handleSearch}
					page={currentPage}
					pageSize={pageSize}
					paginationData={paginationData}
					listLength={transformedData.length}
					handlepageSizeChange={handlePageSizeChange}
					handlePageChange={handlePageChange}
					isDateRangeFilter={false}
					isDisabled={false}
					isFetching={tableLoading || isLoadingMore}
					showExport={true}
					fileName="user-activity-report"
					exportTableData={() => exportGetUserActivityDetails()}
				/>
			</Box>
		);
	};

	return (
		<PageWrapper>
			<Box>
				<TabPanel value={tabValue} index={2}>
					{/* KPI Tiles and Bar Chart Row */}
					<Grid container spacing={1} sx={{ mb: 3 }}>
						{/* Left Side - KPI Tiles */}
						<Grid item xs={12} md={6}>
							{renderKPITiles()}
						</Grid>

						{/* Right Side - Bar Chart */}
						<Grid item xs={12} md={6} id="user-growth-sector">
							{renderUserGrowthChart()}
						</Grid>
					</Grid>

					{/* Line Chart - Full Width */}
					<Box sx={{ mb: 3 }} id="user-activity-trend">
						{renderActivityTrendChart()}
					</Box>

					{/* User Activity Table */}
					<Box sx={{ mt: 4 }} id="user-activity-details">
						{renderUserActivityTable()}
					</Box>
				</TabPanel>

				<TabPanel value={tabValue} index={1}>
					<ReportsTab
						timeRange={timeRange}
						filters={{
							sector: checkedListRef.current.length > 0 ? checkedListRef.current.join(',') : 'all',
							filterOption: timeRange
						}}
						sectorsChange={sectorsChange}
						checkedListRef={checkedListRef}
					/>
				</TabPanel>

				<TabPanel value={tabValue} index={0}>
					<CapacityTab  
						timeRange={timeRange} 
						filters={{ sector: checkedListRef.current.length > 0 ? checkedListRef.current.join(',') : 'all', filterOption: timeRange }} 
						sectorsChange={sectorsChange}
						checkedListRef={checkedListRef}
					/>
				</TabPanel>
			</Box>
		</PageWrapper>
	);
};

export default PowerBIUserActivityDashboard;